/**
 * The only place screens get data from. Reads are React Query hooks; writes go
 * through useWrite()/usePlatformWrite(), which run the call, refresh every
 * query and offer "Rückgängig" as a compensating write.
 *
 * Tenancy (ADR 0004): the active tenant comes from the URL (/v/<slug>/…) and
 * is provided by TenantProvider; useDataSource() is the source of exactly that
 * tenant, and every query key carries the tenant id, so switching clubs can
 * never show cached data of the other one.
 *
 * Which backend: backendFactory.ts, by session mode — the API adapter for a
 * real session, the demo store for the demo, never both. Every query key
 * carries the backend kind as well, so the two can never share a cache entry.
 */
import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { de, formatDamageNumber } from '@fsv/core';
import { ApiError } from '@fsv/shared-client';
import { sessionStore, type SessionState } from '../auth/sessionStore';
import { createBackendFor, type BackendMode } from './backendFactory';
import { useToast } from '../components/Toast';
import { tx } from '@fsv/data';
import { tenantPath } from '../lib/tenantRouting';
import { DataError, type Backend, type DataSource, type PlatformSource } from '@fsv/data';
import type { EventFilter, MembershipRef, SearchQuery, WriteResult } from '@fsv/data';

const BackendContext = createContext<Backend | null>(null);
const TenantContext = createContext<MembershipRef | null>(null);

/** Re-reads memberships (GET /api/me, or the demo store) after start and after writes. Only by the backend of the session's own mode. */
async function refreshMe(backend: Backend): Promise<void> {
  const state = sessionStore.get();
  if (state.status !== 'signed-in' || state.session.mode !== backend.kind) return;
  try {
    sessionStore.setMe(await backend.me());
  } catch {
    /* keep the session as it is */
  }
}

export function DataSourceProvider({ children }: { children: ReactNode }) {
  const state = useSessionState();
  const mode: BackendMode = state.status === 'signed-in' ? state.session.mode : 'signed-out';
  const backend = useMemo(() => createBackendFor(mode), [mode]);
  // Sign-out or a switch between demo and real session: drop what the old backend held (drawing object URLs).
  useEffect(() => () => backend.dispose?.(), [backend]);
  const who = state.status === 'signed-in' ? `${state.session.mode}:${state.session.me.memberId}` : null;
  useEffect(() => {
    if (who) void refreshMe(backend);
  }, [backend, who]);
  return createElement(BackendContext.Provider, { value: backend }, children);
}

export function TenantProvider({ tenant, children }: { tenant: MembershipRef; children: ReactNode }) {
  return createElement(TenantContext.Provider, { value: tenant }, children);
}

export function useBackend(): Backend {
  const b = useContext(BackendContext);
  if (!b) throw new Error('useBackend outside DataSourceProvider');
  return b;
}

/** The active membership; only inside /v/:slug/… */
export function useTenant(): MembershipRef {
  const t = useContext(TenantContext);
  if (!t) throw new Error('useTenant outside a tenant route');
  return t;
}

export function useOptionalTenant(): MembershipRef | null {
  return useContext(TenantContext);
}

/**
 * Damage references of the active tenant: useDamageRef()(42) → "FSV-000042".
 * Every screen that shows a damage number goes through this, so the code is
 * never forgotten and never another club's. A membership from a session
 * stored before codes existed has none; then the bare number is shown until
 * the next GET /api/me.
 */
export function useDamageRef(): (n: number) => string {
  const { code } = useTenant();
  return useCallback((n: number) => formatDamageNumber(n, code || undefined), [code]);
}

/** Path inside the active tenant: useTenantPath()('/suche') → /v/fsv1910/suche. */
export function useTenantPath(): (path?: string) => string {
  const { slug } = useTenant();
  return useCallback((path?: string) => tenantPath(slug, path), [slug]);
}

export function useDataSource(): DataSource {
  const backend = useBackend();
  const { tenantId } = useTenant();
  return useMemo(() => backend.forTenant(tenantId), [backend, tenantId]);
}

export function useSessionState(): SessionState {
  return useSyncExternalStore(sessionStore.subscribe, sessionStore.get);
}

/** Signed-in session; screens behind RequireSession can rely on it. */
export function useSession() {
  const state = useSessionState();
  if (state.status !== 'signed-in') throw new Error('useSession without a session');
  return state.session;
}

// Query keys share the 'fsv' root; a write invalidates the whole tree. The
// data is small (hundreds of rows) and correctness beats cleverness here.
const k = {
  t: (kind: Backend['kind'], tenantId: string) => ['fsv', kind, 't', tenantId] as const,
  platformTenants: (kind: Backend['kind']) => ['fsv', kind, 'platform', 'tenants'] as const,
  platformTenant: (kind: Backend['kind'], id: string) => ['fsv', kind, 'platform', 'tenant', id] as const,
  platformContracts: (kind: Backend['kind']) => ['fsv', kind, 'platform', 'contracts'] as const,
  platformContract: (kind: Backend['kind'], id: string) => ['fsv', kind, 'platform', 'contract', id] as const,
};

/** A query of the active tenant; the key always starts with the tenant id. */
function useTenantQuery<T>(key: readonly unknown[], fn: (s: DataSource) => Promise<T>, extra: { enabled?: boolean; staleTime?: number; retry?: false; keepPrevious?: boolean } = {}) {
  const s = useDataSource();
  return useQuery({
    queryKey: [...k.t(s.kind, s.tenantId), ...key],
    queryFn: () => fn(s),
    ...(extra.enabled !== undefined ? { enabled: extra.enabled } : {}),
    ...(extra.staleTime !== undefined ? { staleTime: extra.staleTime } : {}),
    ...(extra.retry !== undefined ? { retry: extra.retry } : {}),
    ...(extra.keepPrevious ? { placeholderData: keepPreviousData } : {}),
  });
}

export const useSettings = () => useTenantQuery(['settings'], (s) => s.getSettings());
export const useAircraftTypes = () => useTenantQuery(['types'], (s) => s.listAircraftTypes(), { staleTime: Infinity });
export const useFleet = () => useTenantQuery(['fleet'], (s) => s.listFleet());
export const useAircraftList = () => useTenantQuery(['aircraft'], (s) => s.listAircraft());
export const useDamages = (registration: string) => useTenantQuery(['damages', registration], (s) => s.listDamages(registration));
export const useDamage = (id: string) => useTenantQuery(['damage', id], (s) => s.getDamage(id), { retry: false });
export const useSearch = (query: SearchQuery, enabled: boolean) => useTenantQuery(['search', query], (s) => s.search(query), { enabled, keepPrevious: true });
export const useInbox = (enabled = true) => useTenantQuery(['inbox'], (s) => s.inbox(), { enabled });
export const useMyReports = () => useTenantQuery(['mine'], (s) => s.myReports());
export const useMembers = () => useTenantQuery(['members'], (s) => s.listMembers());
export const useEvents = (filter: EventFilter) => useTenantQuery(['events', filter], (s) => s.listEvents(filter), { keepPrevious: true });
export const useTrash = () => useTenantQuery(['trash'], (s) => s.listTrash());
export const useClub = () => useTenantQuery(['club'], (s) => s.getClub());
export const useClubContact = () => useTenantQuery(['club-contact'], (s) => s.getClubContact(), { staleTime: 5 * 60_000 });
export const useFleetAdmin = () => useTenantQuery(['fleet-admin'], (s) => s.listFleetAdmin());
export const useFleetTypes = () => useTenantQuery(['fleet-types'], (s) => s.listFleetTypes());
export const useExports = () => useTenantQuery(['exports'], (s) => s.listExports());
/** The club's active notices of the technicians (0022); fleet marks and the aircraft page read the same list. */
export const useNotices = () => useTenantQuery(['notices'], (s) => s.listNotices());
export const useNoticeHistory = (aircraftId: string, enabled: boolean) =>
  useTenantQuery(['notice-history', aircraftId], (s) => s.listNoticeHistory(aircraftId), { enabled });

/** Inbox count for the navigation badge; usable outside the tenant context (same key as useInbox). */
export function useInboxBadge(tenant: MembershipRef | null, enabled: boolean) {
  const b = useBackend();
  return useQuery({
    queryKey: [...k.t(b.kind, tenant?.tenantId ?? ''), 'inbox'],
    queryFn: () => b.forTenant(tenant?.tenantId ?? '').inbox(),
    enabled: enabled && tenant !== null,
  });
}

export const usePlatformTenants = () => {
  const b = useBackend();
  return useQuery({ queryKey: k.platformTenants(b.kind), queryFn: () => b.platform.listTenants() });
};
/** GET platform/fleet-counts (0025): every club's billable fleet at the 1st of `month`. */
export const usePlatformBilling = (month: string) => {
  const b = useBackend();
  return useQuery({ queryKey: [...k.platformContracts(b.kind), 'billing', month], queryFn: () => b.platform.billing(month), retry: false });
};
export const usePlatformTenant = (tenantId: string | undefined) => {
  const b = useBackend();
  return useQuery({ queryKey: k.platformTenant(b.kind, tenantId ?? ''), queryFn: () => b.platform.getTenant(tenantId ?? ''), enabled: Boolean(tenantId), retry: false });
};
export const usePlatformContracts = () => {
  const b = useBackend();
  return useQuery({ queryKey: k.platformContracts(b.kind), queryFn: () => b.platform.listContracts() });
};
export const usePlatformContract = (id: string | undefined) => {
  const b = useBackend();
  return useQuery({ queryKey: k.platformContract(b.kind, id ?? ''), queryFn: () => b.platform.getContract(id ?? ''), enabled: Boolean(id), retry: false });
};
export const usePlatformExports = (tenantId: string) => {
  const b = useBackend();
  return useQuery({ queryKey: [...k.platformTenant(b.kind, tenantId), 'exports'], queryFn: () => b.platform.listTenantExports(tenantId) });
};

export function errorMessage(error: unknown): string {
  if (error instanceof DataError || error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return de.errors.generic;
}

export interface WriteSpec<R extends WriteResult<unknown>, S = DataSource> {
  run: (source: S) => Promise<R>;
  /** Toast text after success; gets the whole result for extras like `delivered` or photo failures. */
  success: string | ((value: R['value'], result: R) => string);
  /** Compensating write for "Rückgängig"; omit for writes that cannot be undone. */
  undo?: ((source: S, result: R) => Promise<unknown>) | undefined;
  /** Called after the error toast, e.g. to mark refused photos in the form. */
  onError?: ((error: unknown) => void) | undefined;
  /**
   * Errors the screen answers itself instead of with a toast — a 409
   * `pins_drift` becomes a question, a refusal text stands in the dialog.
   * onError still runs.
   */
  quietError?: ((error: unknown) => boolean) | undefined;
}

/** A write refused because the record changed since it was read (API 409 on updated_at). */
export const isStale = (e: unknown): boolean => e instanceof DataError && e.code === 'stale';

/**
 * Runs a write immediately (no delayed commit: a closed tab must not lose it),
 * refreshes all data and the memberships, and shows the toast. Returns null on
 * failure, after the error toast is already shown.
 */
function useWriteFor<S>(source: S) {
  const backend = useBackend();
  const qc = useQueryClient();
  const toast = useToast();
  return useCallback(
    async <R extends WriteResult<unknown>>(spec: WriteSpec<R, S>): Promise<R | null> => {
      try {
        const result = await spec.run(source);
        await qc.invalidateQueries({ queryKey: ['fsv'] });
        await refreshMe(backend);
        const message = typeof spec.success === 'function' ? spec.success(result.value, result) : spec.success;
        const undo = spec.undo;
        toast.show(message, {
          undo: undo
            ? async () => {
                try {
                  await undo(source, result);
                  await qc.invalidateQueries({ queryKey: ['fsv'] });
                  toast.show(tx.toast.undone);
                } catch (e) {
                  if (isStale(e)) await qc.invalidateQueries({ queryKey: ['fsv'] });
                  toast.show(`${tx.toast.undoFailed} ${errorMessage(e)}`, { tone: 'error' });
                }
              }
            : undefined,
        });
        return result;
      } catch (e) {
        // Stale: someone else wrote in between. Reload so the screen shows what is there now.
        if (isStale(e)) await qc.invalidateQueries({ queryKey: ['fsv'] });
        if (!spec.quietError?.(e)) toast.show(errorMessage(e), { tone: 'error' });
        spec.onError?.(e);
        return null;
      }
    },
    [source, backend, qc, toast],
  );
}

/** Write against the active tenant. */
export function useWrite() {
  return useWriteFor<DataSource>(useDataSource());
}

/** Write against /api/platform/*. */
export function usePlatformWrite() {
  return useWriteFor<PlatformSource>(useBackend().platform);
}
